import {Component, EventEmitter, HostListener, Inject, Input, Output, ViewChild} from '@angular/core';
import {
  ApplicationFormSettings, AssignChipNumbersResponse,
  Competitor,
  CompetitorFilter, Country,
  EventItem,
  Lookups,
  PagedFilter,
  Pages,
  Race, RaceAdditionalPricingItem, RaceCommercialItem,
  RaceSettings, RegistrationDisplayFlags, VisibilityTypes,
  AssignChipNumbersRequest, TOAST_STATE
} from "@model";
import {DOCUMENT} from '@angular/common';
import {
  AbstractComponent,
  AssignChipNumbersModalComponent, ConfirmationModalComponent, ImportCompetitorsModalComponent
} from '@components';
import {ActivatedRoute, Router} from '@angular/router';
import {REGISTER_TO_RACE} from "../../../../../../app-routing.module";
import {CompetitorService, ExportService, LoaderService, ToastService} from '@services';
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {TranslocoService} from "@ngneat/transloco";

@Component({
  selector: 'app-race-competitors-list-component',
  templateUrl: './race-competitors-list-component.component.html',
  styleUrls: ['./race-competitors-list-component.component.scss']
})
export class RaceCompetitorsListComponentComponent extends AbstractComponent {
  @Input() competitors: Competitor[] = [];
  @Input() race: Race | undefined;
  @Input() eventItem: EventItem | undefined;
  @Input() genders: Lookups[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];
  @Input() raceSettings: RaceSettings | undefined;
  @Input() applicationFormSettings: ApplicationFormSettings | undefined;
  @Output() printStartList: EventEmitter<void> = new EventEmitter<void>();
  @Output() refreshCounters: EventEmitter<void> = new EventEmitter<void>();
  @Input() raceCommercialItems: RaceCommercialItem[] = []
  @Input() raceAdditionalItems: RaceAdditionalPricingItem[] = []

  showFabButton: boolean = false;
  selectedFile: string | undefined;
  filter: PagedFilter<CompetitorFilter> | undefined;
  category: number | undefined = undefined;
  name: string | undefined;
  gender: number | undefined = undefined;
  page: number = 1;

  constructor(@Inject(DOCUMENT) private document: Document, private modalService: NgbModal,
              private route: ActivatedRoute, private router: Router, private exportService: ExportService,
              private translocoService: TranslocoService, private toastService: ToastService,
              private competitorService: CompetitorService, private loaderService: LoaderService) {
    super();
  }

  handleFilterChange(filter: PagedFilter<CompetitorFilter> | undefined): void {
    this.filter = filter;
  }

  @HostListener('window:scroll')
  toggleFabButton(): void {
    this.showFabButton = window.pageYOffset != 0;
  }


  openImportModal() {
    const modalRef: NgbModalRef = this.modalService.open(ImportCompetitorsModalComponent,
        {
          centered: true,
          size: 'lg'
        });
    modalRef.componentInstance.race = this.race;
    modalRef.componentInstance.onSaveSuccessAction.subscribe(() => {
      this.onRefreshCounters();
      this.loadCompetitors();
    })
  }

  loadCompetitors() {
    this.competitorService.findCompetitors(new PagedFilter(new Pages(), {
      raceId: this.race?.id,
      eventId: this.eventItem?.id
    }), true, false).subscribe((value) => {
          this.loaderService.dismissLoading();
          this.competitors = value;
          this.filter = new PagedFilter(new Pages(undefined, this.page), new CompetitorFilter())
        }
    );
  }


  onRegister() {
    const openForRegistration: Race[] = this.eventItem?.racesList?.filter(r => !!r.organiserOpen) || [];

    if (openForRegistration.length > 0) {
      this.router.navigate([REGISTER_TO_RACE], {
        relativeTo: this.route,
        queryParams: {
          eventId: this.eventItem!.id,
          raceId: openForRegistration[0].id
        }
      });
    }
  }

  isRaceRegistrationEnabled() {
    return (!!this.race?.organiserOpen);
  }


  downloadCompetitorsListForRace(type: string) {
    if (this.filter === undefined || !this.filter) {
      let filter: CompetitorFilter = new CompetitorFilter();
      if (this.category) {
        filter.categories = [this.category];
      }
      filter.gender = this.gender ?? undefined;
      filter.name = this.name;
      filter.eventId = +this.route.snapshot.queryParams['eventId'];
      filter.raceId = +this.route.snapshot.queryParams['raceId'];
      this.filter = new PagedFilter(new Pages(undefined, this.page), filter);
    }
    if (this.race) {
      this.exportService.downloadCompetitorsListForRace(type, this.filter, this.race!);
    }
  }

  assignChipNumber() {
    const modalRef = this.modalService.open(AssignChipNumbersModalComponent, {
      centered: true,
      size: 'lg',
    });
    modalRef.componentInstance.title = this.translocoService.translate("race.management.competitors.assign.chip.numbers");
    modalRef.componentInstance.event = this.eventItem;
    modalRef.componentInstance.race = this.race;
    modalRef.result.then((request: AssignChipNumbersRequest) => {
      this.onAssignChipNumber(request);
    }, (_) => {
      /// ignore
    });
  }

  onAssignChipNumber(request: AssignChipNumbersRequest): void {
    this.loaderService.startLoading();
    const onSuccess = (result: AssignChipNumbersResponse) => {
      this.loaderService.dismissLoading();
      const { state, message } = AssignChipNumbersResponse.buildToastMessage(
          result,
          (key) => this.translocoService.translate(key)
      );

      this.toastService.showToast(state, message);
      this.loadCompetitors();
    }
    const onError = (result: number | undefined) => {
      this.loaderService.dismissLoading();
      if (result === CompetitorService.INVALID_CHIP_NUMBER) {
        this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("race.management.competitors.assign.chip.invalid.chip.number.error"));
      } else {
        this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("race.management.competitors.assign.chip.numbers.error"));
      }
    }

    request.raceIds = [this.race!.id!];
    this.competitorService.assignChipNumbers(this.eventItem?.id, request, onSuccess, onError);
  }

  trackByFn(index: number, item: Competitor): number | undefined {
    return item.id;
  }

  scrollToTop(): void {
    return this.document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    });
  }

  onPrintStartList() {
    this.printStartList.next();
  }

  onRefreshCounters() {
    this.refreshCounters.emit();
  }

  protected readonly VisibilityTypes = VisibilityTypes;
  protected readonly RegistrationDisplayFlags = RegistrationDisplayFlags;
}
